// ValidationReviewModal.js - Review modal for event validation before publish
import React, { useState, useEffect } from "react";
import { FaTimes, FaExclamationTriangle, FaCheckCircle, FaEdit, FaChevronDown, FaChevronUp, FaSave, FaTimesCircle } from "react-icons/fa";

/**
 * ValidationReviewModal
 * Shows all validation issues grouped by step, allows inline editing and navigation to fix them
 * Required for publish mode, optional for draft mode
 */
const ValidationReviewModal = ({
  isOpen,
  onClose,
  validationResults,
  formData,
  setFormData,
  isPublishMode,
  onGoToStep,
  onConfirm,
  isSubmitting,
  isLong,
  mediaDraft,
}) => {
  const [expandedSections, setExpandedSections] = useState({
    eventDetails: true,
    additionalDetails: true,
    sponsors: true,
    template: true,
    media: true,
  });
  
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");

  // Reset editing state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEditingField(null);
      setEditValue("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Helper to check if a value is empty
  const isEmpty = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === "string" && value.trim() === "") return true;
    if (Array.isArray(value) && value.length === 0) return true;
    return false;
  };

  // Calculate validation summary from validation results
  const allIssues = validationResults || [];
  const criticalIssues = allIssues.filter((i) => i.severity === "critical");
  const hasBlockingIssues = isPublishMode && criticalIssues.length > 0;

  // Group issues by step
  const step1Issues = allIssues.filter((i) => i.step === 1);
  const step2Issues = allIssues.filter((i) => i.step === 2);
  const step3Issues = allIssues.filter((i) => i.step === 3);
  const step4Issues = allIssues.filter((i) => i.step === 4);
  const step5Issues = allIssues.filter((i) => i.step === 5);

  // Check if field is empty for display purposes
  const isFieldEmpty = (fieldName) => {
    switch (fieldName) {
      case "eventType":
        return isEmpty(formData.eventType);
      case "eventName":
        return isEmpty(formData.eventName);
      case "description":
        return isEmpty(formData.description);
      case "termsConditions":
        return isEmpty(formData.termsConditions);
      case "instructions":
        return isEmpty(formData.instructions);
      case "startDate":
        return isEmpty(formData.startDate);
      case "endDate":
        return isEmpty(formData.endDate);
      case "votingDuration":
        return isEmpty(formData.votingDuration);
      case "eventCaption1":
        return isEmpty(formData.additionalData?.eventCaption1);
      case "eventCaption2":
        return isEmpty(formData.additionalData?.eventCaption2);
      case "eventHashtags":
        return isEmpty(formData.additionalData?.eventHashtags);
      case "eventPrice":
        return isEmpty(formData.additionalData?.eventPrice);
      case "eventImageUrl":
        return isEmpty(formData.additionalData?.eventImageUrl);
      case "advideoUrl":
        return isEmpty(formData.additionalData?.advideoUrl);
      case "demoVideoUrl":
        return isEmpty(formData.additionalData?.demoVideoUrl);
      case "sponsorData":
        return isEmpty(formData.sponsorData);
      case "templateData":
        return isEmpty(formData.templateData);
      case "mediaRequired":
        return isEmpty(mediaDraft?.imageData) && isEmpty(mediaDraft?.videoData) && isEmpty(mediaDraft?.musicData);
      case "imageData":
        return isEmpty(mediaDraft?.imageData);
      case "videoData":
        return isEmpty(mediaDraft?.videoData);
      case "musicData":
        return isEmpty(mediaDraft?.musicData);
      default:
        return false;
    }
  };

  // Count REQUIRED fields that are missing per step (optional empty fields don't count here)
  const countStepMissingRequired = (step) => {
    // Only fields that are truly required
    const requiredByStep = {
      1: ["eventType", "eventName", "description", "termsConditions", "instructions", "startDate", "endDate"],
      2: ["eventCaption2"],
      3: ["sponsorData"],
      4: ["templateData"],
      5: [],
    };
    return (requiredByStep[step] || []).filter(f => isFieldEmpty(f)).length;
  };

  // Count optional fields that are empty (for info, not blocking)
  const countStepEmptyFields = (step) => {
    const allFields = {
      1: ["eventType", "eventName", "description", "termsConditions", "instructions", "startDate", "endDate", "votingDuration"],
      2: ["eventCaption1", "eventCaption2", "eventHashtags", "eventPrice", "eventImageUrl", "advideoUrl", "demoVideoUrl"],
      3: ["sponsorData"],
      4: ["templateData"],
      5: isLong ? ["imageData", "videoData", "musicData"] : [],
    };
    return (allFields[step] || []).filter(f => isFieldEmpty(f)).length;
  };

  // Handle inline edit start
  const startEditing = (fieldName, currentValue) => {
    setEditingField(fieldName);
    setEditValue(currentValue || "");
  };

  // Handle inline edit save
  const saveEdit = (fieldName) => {
    if (!setFormData) {
      setEditingField(null);
      return;
    }

    // Update formData based on field
    if (["eventCaption1", "eventCaption2", "eventHashtags", "eventPrice"].includes(fieldName)) {
      setFormData(prev => ({
        ...prev,
        additionalData: {
          ...prev.additionalData,
          [fieldName]: editValue,
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [fieldName]: editValue,
      }));
    }
    
    setEditingField(null);
    setEditValue("");
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingField(null);
    setEditValue("");
  };

  const renderFieldValue = (value, fallback = "Not set") => {
    if (value === null || value === undefined || value === "") {
      // Yellow/orange for all empty fields - they need attention
      return <span className="text-yellow-600 font-medium">{fallback}</span>;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-yellow-600 font-medium">None added</span>;
      }
      return <span className="text-gray-700">{value.length} item(s)</span>;
    }
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "string" && value.length > 100) return value.substring(0, 100) + "...";
    return String(value);
  };

  const getFieldClass = (hasIssue, isWarning = false, fieldEmpty = false) => {
    // Show yellow styling for any empty field
    if (fieldEmpty) {
      return "bg-yellow-50 border-yellow-300 border-l-4 border-l-yellow-500";
    }
    // Show error styling for validation issues
    if (hasIssue) {
      return isWarning
        ? "bg-yellow-50 border-yellow-300 border-l-4 border-l-yellow-500"
        : "bg-red-50 border-red-300 border-l-4 border-l-red-500";
    }
    return "bg-green-50 border-green-200 border-l-4 border-l-green-500";
  };

  const getIssueForField = (fieldName) => allIssues.find((i) => i.field === fieldName);

  // Check if a field is editable inline
  const isEditableField = (fieldName) => {
    const editableFields = ["eventName", "description", "termsConditions", "instructions", "eventCaption1", "eventCaption2", "eventHashtags", "eventPrice", "votingDuration"];
    return editableFields.includes(fieldName);
  };

  const renderSectionHeader = (title, sectionKey, issueCount, stepNumber) => {
    const missingRequired = countStepMissingRequired(stepNumber);
    const totalEmpty = countStepEmptyFields(stepNumber);
    const optionalEmpty = totalEmpty - missingRequired;
    const isComplete = missingRequired === 0;

    return (
      <div
        className="flex items-center justify-between p-3 bg-gray-100 rounded-t-lg cursor-pointer hover:bg-gray-200 transition"
        onClick={() => toggleSection(sectionKey)}
      >
        <div className="flex items-center gap-3">
          <h4 className="font-semibold text-gray-800">{title}</h4>
          {missingRequired > 0 ? (
            <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full flex items-center gap-1">
              <FaExclamationTriangle size={10} />
              {missingRequired} required field{missingRequired > 1 ? "s" : ""} missing
            </span>
          ) : optionalEmpty > 0 ? (
            <span className="bg-yellow-100 text-yellow-700 text-xs px-2 py-1 rounded-full flex items-center gap-1">
              {optionalEmpty} optional field{optionalEmpty > 1 ? "s" : ""} not set
            </span>
          ) : (
            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full flex items-center gap-1">
              <FaCheckCircle className="text-green-600" size={10} /> Complete
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onGoToStep(stepNumber);
              onClose();
            }}
            className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1 bg-blue-50 px-2 py-1 rounded"
          >
            <FaEdit size={12} /> Go to Step
          </button>
          {expandedSections[sectionKey] ? <FaChevronUp /> : <FaChevronDown />}
        </div>
      </div>
    );
  };

  const renderFieldRow = (label, value, fieldName, required = false) => {
    const issue = getIssueForField(fieldName);
    const hasIssue = !!issue;
    const isWarning = issue?.severity === "warning";
    const fieldEmpty = isFieldEmpty(fieldName);
    const isEditing = editingField === fieldName;
    const canEdit = isEditableField(fieldName) && setFormData;

    return (
      <div className={`p-3 border rounded mb-2 ${getFieldClass(hasIssue, isWarning, fieldEmpty)}`}>
        <div className="flex justify-between items-start">
          <span className="font-medium text-gray-700">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </span>
          
          {isEditing ? (
            <div className="flex items-center gap-2 flex-1 ml-4">
              {fieldName === "description" || fieldName === "termsConditions" || fieldName === "instructions" ? (
                <textarea
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="flex-1 p-2 border rounded text-sm min-h-[80px]"
                  autoFocus
                />
              ) : (
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="flex-1 p-2 border rounded text-sm"
                  autoFocus
                />
              )}
              <button
                type="button"
                onClick={() => saveEdit(fieldName)}
                className="p-2 bg-green-500 text-white rounded hover:bg-green-600"
                title="Save"
              >
                <FaSave size={12} />
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                className="p-2 bg-gray-400 text-white rounded hover:bg-gray-500"
                title="Cancel"
              >
                <FaTimesCircle size={12} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-right max-w-[50%]">{renderFieldValue(value)}</span>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => startEditing(fieldName, value)}
                  className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                  title="Edit inline"
                >
                  <FaEdit size={12} />
                </button>
              )}
            </div>
          )}
        </div>
        {(hasIssue || fieldEmpty) && !isEditing && (
          <p className={`text-sm mt-1 ${fieldEmpty ? "text-yellow-700" : isWarning ? "text-yellow-700" : "text-red-600"}`}>
            <FaExclamationTriangle className="inline mr-1" size={12} />
            {issue?.message || "This field is not set"}
          </p>
        )}
      </div>
    );
  };

  // Calculate total REQUIRED missing fields (for the summary banner)
  const totalRequiredMissing = [1, 2, 3, 4, ...(isLong ? [5] : [])].reduce(
    (sum, step) => sum + countStepMissingRequired(step),
    0
  );
  // Total empty optional fields (for soft info)
  const totalEmptyFields = [1, 2, 3, 4, ...(isLong ? [5] : [])].reduce(
    (sum, step) => sum + countStepEmptyFields(step),
    0
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-600 to-blue-700">
          <div>
            <h2 className="text-xl font-bold text-white">
              {isPublishMode ? "Review Before Publishing" : "Review Event Details"}
            </h2>
            <p className="text-blue-100 text-sm mt-1">
              {isPublishMode
                ? "All fields must be completed to publish"
                : "Review your event details before saving"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-blue-800 rounded-full p-2 transition"
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* Summary Banner */}
        {totalRequiredMissing > 0 && (
          <div className="p-4 bg-red-50 border-b">
            <div className="flex items-center gap-3">
              <FaExclamationTriangle className="text-red-500" size={24} />
              <div>
                <p className="font-semibold text-red-800">
                  {totalRequiredMissing} required field{totalRequiredMissing !== 1 ? "s" : ""} missing
                </p>
                <p className="text-sm text-gray-600">
                  Click the edit icon to fill values inline, or "Go to Step" for images and sponsors.
                </p>
              </div>
            </div>
          </div>
        )}

        {totalRequiredMissing === 0 && totalEmptyFields > 0 && (
          <div className="p-4 bg-yellow-50 border-b">
            <div className="flex items-center gap-3">
              <FaExclamationTriangle className="text-yellow-500" size={24} />
              <div>
                <p className="font-semibold text-yellow-800">
                  {totalEmptyFields} optional field{totalEmptyFields !== 1 ? "s" : ""} not set
                </p>
                <p className="text-sm text-gray-600">
                  All required fields are filled. The optional fields below can be skipped.
                </p>
              </div>
            </div>
          </div>
        )}

        {totalRequiredMissing === 0 && totalEmptyFields === 0 && (
          <div className="p-4 bg-green-50 border-b">
            <div className="flex items-center gap-3">
              <FaCheckCircle className="text-green-500" size={24} />
              <div>
                <p className="font-semibold text-green-800">All fields are complete!</p>
                <p className="text-sm text-gray-600">Your event is ready to be {isPublishMode ? "published" : "saved"}.</p>
              </div>
            </div>
          </div>
        )}

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Step 1: Event Details */}
          <div className="border rounded-lg overflow-hidden">
            {renderSectionHeader("Event Details", "eventDetails", step1Issues.length, 1)}
            {expandedSections.eventDetails && (
              <div className="p-4 bg-white">
                {renderFieldRow("Event Type", formData.eventType, "eventType", true)}
                {renderFieldRow("Event Name", formData.eventName, "eventName", true)}
                {renderFieldRow("Description", formData.description, "description", true)}
                {renderFieldRow("Terms & Conditions", formData.termsConditions, "termsConditions", true)}
                {renderFieldRow("Instructions", formData.instructions, "instructions", true)}
                {renderFieldRow("Start Date", formData.startDate, "startDate", true)}
                {renderFieldRow("End Date", formData.endDate, "endDate", true)}
                {renderFieldRow("Voting Duration", formData.votingDuration ? `${formData.votingDuration} hours` : null, "votingDuration")}
              </div>
            )}
          </div>

          {/* Step 2: Additional Details */}
          <div className="border rounded-lg overflow-hidden">
            {renderSectionHeader("Additional Details", "additionalDetails", step2Issues.length, 2)}
            {expandedSections.additionalDetails && (
              <div className="p-4 bg-white">
                {renderFieldRow("Participation post caption", formData.additionalData?.participationPost?.caption1, "eventCaption1", false)}
                {renderFieldRow("Caption 2", formData.additionalData?.eventCaption2, "eventCaption2", true)}
                {renderFieldRow("Event Price", formData.additionalData?.eventPrice, "eventPrice")}
                {renderFieldRow("Event Image", formData.additionalData?.eventImageUrl ? "Uploaded" : null, "eventImageUrl")}
                {renderFieldRow("Ad Video", formData.additionalData?.advideoUrl ? "Uploaded" : null, "advideoUrl")}
                {renderFieldRow("Demo Video", formData.additionalData?.demoVideoUrl ? "Uploaded" : null, "demoVideoUrl")}
              </div>
            )}
          </div>

          {/* Step 3: Sponsors */}
          <div className="border rounded-lg overflow-hidden">
            {renderSectionHeader("Sponsors", "sponsors", step3Issues.length, 3)}
            {expandedSections.sponsors && (
              <div className="p-4 bg-white">
                {renderFieldRow(
                  "Sponsors",
                  formData.sponsorData?.length > 0 ? `${formData.sponsorData.length} sponsor(s) added` : null,
                  "sponsorData",
                  true
                )}
                {formData.sponsorData?.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {formData.sponsorData.map((sponsor, idx) => (
                      <div key={idx} className="bg-gray-50 p-2 rounded text-sm">
                        <span className="font-medium">{sponsor.name || sponsor.sponsorName || `Sponsor ${idx + 1}`}</span>
                        {sponsor.instagram && <span className="text-gray-500 ml-2">@{sponsor.instagram}</span>}
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  <FaEdit className="inline mr-1" size={10} />
                  To add or edit sponsors, click "Go to Step" above
                </p>
              </div>
            )}
          </div>

          {/* Step 4: Template */}
          <div className="border rounded-lg overflow-hidden">
            {renderSectionHeader("Template", "template", step4Issues.length, 4)}
            {expandedSections.template && (
              <div className="p-4 bg-white">
                {renderFieldRow("Template Image", formData.templateData ? "Uploaded" : null, "templateData", true)}
                {formData.templateData && (
                  <div className="mt-2">
                    <img
                      src={formData.templateData}
                      alt="Template Preview"
                      className="w-32 h-32 object-cover rounded border"
                    />
                  </div>
                )}
                {!formData.templateData && (
                  <p className="text-xs text-gray-500 mt-2">
                    <FaEdit className="inline mr-1" size={10} />
                    To upload a template, click "Go to Step" above
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Step 5: Media (Long events only) */}
          {isLong && (
            <div className="border rounded-lg overflow-hidden">
              {renderSectionHeader("Media", "media", step5Issues.length, 5)}
              {expandedSections.media && (
                <div className="p-4 bg-white">
                  {renderFieldRow(
                    "Images",
                    mediaDraft?.imageData?.length > 0 ? `${mediaDraft.imageData.length} image(s)` : null,
                    "imageData"
                  )}
                  {renderFieldRow(
                    "Videos",
                    mediaDraft?.videoData?.length > 0 ? `${mediaDraft.videoData.length} video(s)` : null,
                    "videoData"
                  )}
                  {renderFieldRow(
                    "Music",
                    mediaDraft?.musicData?.length > 0 ? `${mediaDraft.musicData.length} track(s)` : null,
                    "musicData"
                  )}
                  {renderFieldRow("Media Content", 
                    (mediaDraft?.imageData?.length > 0 || mediaDraft?.videoData?.length > 0 || mediaDraft?.musicData?.length > 0) 
                      ? "Has content" 
                      : null, 
                    "mediaRequired", 
                    true
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    <FaEdit className="inline mr-1" size={10} />
                    To add media, click "Go to Step" above
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
          >
            Cancel
          </button>
          <div className="flex items-center gap-3">
            {!isPublishMode && totalRequiredMissing > 0 && (
              <span className="text-sm text-orange-600">Saving as draft — {totalRequiredMissing} required field{totalRequiredMissing !== 1 ? "s" : ""} missing</span>
            )}
            {!isPublishMode && totalRequiredMissing === 0 && totalEmptyFields > 0 && (
              <span className="text-sm text-gray-500">Saving as draft with some optional fields empty</span>
            )}
            <button
              type="button"
              onClick={onConfirm}
              disabled={hasBlockingIssues || isSubmitting}
              className={`px-6 py-2 rounded-lg font-medium transition flex items-center gap-2 ${
                hasBlockingIssues
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : isPublishMode
                    ? "bg-green-600 text-white hover:bg-green-700"
                    : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin">⏳</span>
                  {isPublishMode ? "Publishing..." : "Saving..."}
                </>
              ) : hasBlockingIssues ? (
                "Complete Required Fields"
              ) : isPublishMode ? (
                "Publish Event"
              ) : (
                "Save as Draft"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValidationReviewModal;
